import React, { useState } from "react";
import { useLang } from "@/lib/lang";
import { api, useAuth } from "@/lib/auth";
import { Mic, Trophy, Camera, Eye, ArrowRight, X } from "lucide-react";

export default function OnboardingModal({ onClose }) {
    const { t } = useLang();
    const { refresh } = useAuth();
    const [step, setStep] = useState(0);

    const steps = [
        {
            icon: <Mic size={28} className="text-brand" />,
            title: t("onboarding.step1_title"),
            body: t("onboarding.step1_body"),
        },
        {
            icon: <Camera size={28} className="text-brand" />,
            title: t("onboarding.step2_title"),
            body: t("onboarding.step2_body"),
        },
        {
            icon: <Trophy size={28} className="text-brand" />,
            title: t("onboarding.step3_title"),
            body: t("onboarding.step3_body"),
        },
        {
            icon: <Eye size={28} className="text-brand" />,
            title: t("onboarding.step4_title"),
            body: t("onboarding.step4_body"),
        },
    ];

    const finish = async () => {
        try {
            await api.post("/auth/onboarded");
        } catch (e) {
            // non-fatal: don't block the user from continuing even if this fails
        }
        await refresh();
        onClose();
    };

    const isLast = step === steps.length - 1;
    const current = steps[step];

    return (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-6" data-testid="onboarding-modal">
            <div className="bg-white max-w-md w-full rounded-lg border border-slate-200 relative">
                <button
                    onClick={finish}
                    className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"
                    data-testid="onboarding-skip"
                    aria-label={t("onboarding.skip")}
                >
                    <X size={18} />
                </button>

                <div className="p-8">
                    <div className="w-14 h-14 rounded-full bg-brand/10 flex items-center justify-center mb-6">
                        {current.icon}
                    </div>
                    <div className="mono-label text-slate-400 mb-2">
                        {step + 1} / {steps.length}
                    </div>
                    <h2 className="font-display text-2xl mb-3 tracking-tight" data-testid="onboarding-title">
                        {current.title}
                    </h2>
                    <p className="text-sm text-slate-600 leading-relaxed mb-8">{current.body}</p>

                    <div className="flex items-center justify-between">
                        <div className="flex gap-1.5">
                            {steps.map((_, i) => (
                                <span
                                    key={i}
                                    className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-brand" : "w-1.5 bg-slate-200"}`}
                                />
                            ))}
                        </div>
                        {isLast ? (
                            <button onClick={finish} className="btn-primary" data-testid="onboarding-finish">
                                {t("onboarding.start")} <ArrowRight size={16} />
                            </button>
                        ) : (
                            <button onClick={() => setStep((s) => s + 1)} className="btn-primary" data-testid="onboarding-next">
                                {t("onboarding.next")} <ArrowRight size={16} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
